/**
 * Разделяемые GET'ы, которые раньше каждый компонент делал сам.
 *
 * ЗАЧЕМ (перф-аудит карты 14.08, корень 6 — «болтливость»):
 * за одну сессию уходило `/session/season` ×124, `/seasons` ×124,
 * `/users/me/map-preferences` ×60, `/scope/available` ×48 — все ответы
 * идентичные. Причина не в react-query, а в том, что эти четыре ручки
 * дёргались голым `fetch`/`api.get` из эффектов разных компонентов: общего
 * кэша не было, дедупликации in-flight запросов тоже.
 *
 * Здесь они проходят через `queryClient.fetchQuery` — тот же кэш, что у
 * остального UI. Это даёт разом:
 *   · dedupe — два компонента, стартовавшие запрос одновременно, получают ОДИН
 *     сетевой вызов и общий промис;
 *   · staleTime — повтор внутри окна свежести отдаётся из памяти без сети.
 *
 * Обоснование окон свежести (данные меняются редко и всегда — явным действием
 * пользователя, после которого мы инвалидируем ключ руками):
 *   · scope/available — состав холдинга/ферм. Меняется при правке оргструктуры,
 *     не в фоне. 5 мин + инвалидация после `POST /scope/set`.
 *   · session/season, seasons — активный сезон и список сезонов. 1 мин, см.
 *     подробное обоснование у `SEASON_STALE_MS` (там баланс с AK-542).
 *   · map-preferences — настройки карты этого же пользователя, пишем их мы же
 *     (PATCH), читаем один раз на загрузку карты. 10 мин + инвалидация на
 *     запись.
 *   · scope/extents — bbox скоупа, производный от геометрии полей. 5 мин.
 *
 * Инвариант: это НЕ «отключить обновление». Живость сезона (AK-542: фокус
 * окна, возврат вкладки, таймер 10 мин) сохранена — просто теперь она не
 * превращается в шторм одинаковых запросов.
 */
import { queryClient } from '@/lib/queryClient';
import { api } from '@/lib/api';

const FIVE_MIN = 5 * 60_000;
const TEN_MIN = 10 * 60_000;

/**
 * Окно свежести сезона — намеренно КОРОТКОЕ (минута), в отличие от остальных
 * ключей.
 *
 * Здесь мы приземляемся между двумя прошлыми решениями. AK-542 добавил
 * перезапрос сезона на фокус окна/возврат вкладки, потому что вкладка ФМС
 * живёт у агронома неделями и «снимок на mount» держал неверный сезон до
 * ручного F5. Отменять это нельзя. Но `focus` в браузере срабатывает на каждый
 * возврат в окно — хоть из devtools, хоть после alt-tab, — и за сессию это
 * дало по 124 запроса на `/session/season` и `/seasons`.
 *
 * Минута решает обе задачи: серия фокусов подряд схлопывается в один запрос,
 * а реальный уход-возврат (обед, соседняя вкладка, починка данных на сервере)
 * всё так же перечитывает сезон. Расхождение, ради которого писался AK-542,
 * измерялось днями, а не секундами.
 */
export const SEASON_STALE_MS = 60_000;

export const sharedQueryKeys = {
  scopeAvailable: ['shared', 'scope', 'available'] as const,
  scopeExtents: (scopeType: string, scopeId: string) =>
    ['shared', 'scope', 'extents', scopeType, scopeId] as const,
  activeSeason: ['shared', 'season', 'active'] as const,
  seasonsList: (companyId?: string) =>
    ['shared', 'season', 'list', companyId ?? null] as const,
  seasonConfig: (companyId?: string) =>
    ['shared', 'season', 'config', companyId ?? null] as const,
  mapPreferences: ['shared', 'map', 'preferences'] as const,
};

/** `GET /api/v1/scope/available` — общий кэш для селектора скоупа и карты. */
export function fetchScopeAvailable<T>(): Promise<T> {
  return queryClient.fetchQuery<T>({
    queryKey: sharedQueryKeys.scopeAvailable,
    queryFn: () => api.get<T>('/api/v1/scope/available'),
    staleTime: FIVE_MIN,
  });
}

/** Сбросить кэш скоупа — после `POST /scope/set` и прочих правок оргструктуры. */
export function invalidateScopeAvailable(): void {
  void queryClient.invalidateQueries({ queryKey: sharedQueryKeys.scopeAvailable });
}

export interface ScopeExtentsPayload {
  bbox?: [number, number, number, number];
  [k: string]: unknown;
}

/**
 * `GET /api/v1/scope/extents/{type}/{id}` с negative-cache.
 *
 * 404 здесь — ШТАТНЫЙ ответ: у холдинга без полей нет ни одного контура, значит
 * нет и bbox. До этого фикса такой 404 уходил в error-путь react-query/повторный
 * эффект и ретраился до 14 раз за сессию. Теперь «нет данных» — это значение
 * `null`, которое кэшируется как обычный успех, а `retry: false` гарантирует,
 * что мы не долбим сервер ради заведомо того же ответа.
 *
 * Сетевые сбои (offline, 5xx) остаются ошибкой, но наружу отдаются как `null` —
 * вызывающий код (fitBounds / индикатор «уехал вид») и раньше молча жил без
 * extents, ронять из-за них карту нельзя.
 */
export function fetchScopeExtents(
  scopeType: string,
  scopeId: string | null,
): Promise<ScopeExtentsPayload | null> {
  const id = scopeId ?? '-';
  return queryClient
    .fetchQuery<ScopeExtentsPayload | null>({
      queryKey: sharedQueryKeys.scopeExtents(scopeType, id),
      staleTime: FIVE_MIN,
      retry: false,
      queryFn: async () => {
        const r = await fetch(`/api/v1/scope/extents/${scopeType}/${id}`, {
          credentials: 'include',
        });
        // 404 = «полей нет» → кэшируем пустоту, а не ошибку.
        if (r.status === 404) return null;
        if (!r.ok) throw new Error(`scope/extents ${r.status}`);
        return (await r.json()) as ScopeExtentsPayload;
      },
    })
    .catch(() => null);
}

/** `GET /api/v1/users/me/map-preferences` — читается один раз на загрузку карты. */
export function fetchMapPreferences<T>(): Promise<T | null> {
  return queryClient
    .fetchQuery<T | null>({
      queryKey: sharedQueryKeys.mapPreferences,
      staleTime: TEN_MIN,
      retry: false,
      queryFn: async () => {
        const r = await fetch('/api/v1/users/me/map-preferences', {
          credentials: 'include',
        });
        if (!r.ok) return null;
        return (await r.json()) as T;
      },
    })
    .catch(() => null);
}

/** Сбросить кэш настроек карты — после нашего же PATCH. */
export function invalidateMapPreferences(): void {
  void queryClient.invalidateQueries({ queryKey: sharedQueryKeys.mapPreferences });
}

/**
 * Сезон: активный + список. `force` используется после явного действия
 * пользователя (смена сезона, снятие замка редактирования) — там ждать
 * истечения окна свежести нельзя.
 */
export function fetchSharedSeason<A, L>(
  loadActive: () => Promise<A>,
  loadList: () => Promise<L>,
  companyId?: string,
  force = false,
): Promise<[A, L]> {
  if (force) {
    queryClient.removeQueries({ queryKey: sharedQueryKeys.activeSeason });
    queryClient.removeQueries({ queryKey: sharedQueryKeys.seasonsList(companyId) });
  }
  return Promise.all([
    queryClient.fetchQuery<A>({
      queryKey: sharedQueryKeys.activeSeason,
      queryFn: loadActive,
      staleTime: SEASON_STALE_MS,
    }),
    queryClient.fetchQuery<L>({
      queryKey: sharedQueryKeys.seasonsList(companyId),
      queryFn: loadList,
      staleTime: SEASON_STALE_MS,
    }),
  ]);
}

/**
 * Настройка сезона компании (`/company/season-config`) для ШАПКИ — окно
 * видимости сезонов (AK-790 re-land #673). Идёт через тот же кэш, что и
 * сезон: иначе SeasonPicker тянул бы настройку отдельно, и «одна настройка /
 * два потребителя» стало бы «три запроса на одну строку».
 *
 * Штатные 4xx (нет компании, несколько компаний без выбора) — это `null`, а
 * не ошибка: окна нет, селектор показывает все сезоны, как до фикса. Ретраев
 * нет по той же причине — 4xx повторится тем же ответом. Кэшировать `null`
 * нельзя (иначе выбор компании в настройках не пробился бы сюда до истечения
 * окна свежести), поэтому отказ ловится СНАРУЖИ `fetchQuery`.
 *
 * `force` — явное действие пользователя (сохранил настройки сезона): ждать
 * истечения окна свежести нельзя.
 */
export function fetchSharedSeasonConfig<C>(
  loadConfig: () => Promise<C>,
  companyId?: string,
  force = false,
): Promise<C | null> {
  const queryKey = sharedQueryKeys.seasonConfig(companyId);
  if (force) queryClient.removeQueries({ queryKey });
  return queryClient
    .fetchQuery<C>({ queryKey, queryFn: loadConfig, staleTime: FIVE_MIN, retry: false })
    .catch(() => null);
}
